import type { PositionSample } from '../sensors/position';

/**
 * フェーズ0（実機検証）用の診断パネル。
 * 「Web で カーナビが成立するか」を実機で判断するための数値を常時表示する。
 * データは端末内だけ（サーバーへ送らない）。「ログ保存」で JSON を端末に書き出せる。
 */
export class Diagnostics {
  private readonly el: HTMLElement;
  private readonly fields = new Map<string, HTMLElement>();
  private readonly logEl: HTMLElement;
  private readonly logs: { t: string; msg: string }[] = [];
  private lastSampleAt: number | null = null;
  private intervals: number[] = [];
  private hiddenAt: number | null = null;
  private frames = 0;
  private fpsAt = performance.now();

  /** label を関数にすると、押すたびに表示を更新する（「テスト走行 4倍速」の切り替えなど） */
  constructor(root: HTMLElement, actions: { label: string | (() => string); run: () => void }[]) {
    this.el = document.createElement('section');
    this.el.className = 'diag';
    this.el.hidden = true;
    this.el.innerHTML = `
      <header><strong>診断</strong><button class="btn small" data-close aria-label="閉じる">×</button></header>
      <dl></dl>
      <div class="diag-actions"></div>
      <pre class="diag-log"></pre>`;
    root.append(this.el);
    this.logEl = this.el.querySelector('.diag-log')!;
    this.el.querySelector('[data-close]')!.addEventListener('click', () => this.toggle(false));

    const actionsEl = this.el.querySelector('.diag-actions')!;
    for (const a of [...actions, { label: 'ログ保存', run: () => this.download() }]) {
      const b = document.createElement('button');
      b.className = 'btn small';
      const text = () => (typeof a.label === 'function' ? a.label() : a.label);
      b.textContent = text();
      b.addEventListener('click', () => {
        a.run();
        b.textContent = text();
      });
      actionsEl.append(b);
    }

    [
      ['mode', '起動形態'],
      ['secure', 'HTTPS'],
      ['source', '位置ソース'],
      ['interval', '更新間隔 平均/最大'],
      ['accuracy', '精度'],
      ['speed', '速度'],
      ['gpsHeading', 'GPS 方位'],
      ['compass', 'コンパス'],
      ['offset', 'ルートからのずれ'],
      ['wake', 'スリープ防止'],
      ['voice', '音声'],
      ['fps', '描画 FPS'],
      ['gl', 'GPU'],
    ].forEach(([k, label]) => this.addField(k, label));

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    this.set('mode', standalone ? 'ホーム画面（PWA）' : 'ブラウザ');
    this.set('secure', window.isSecureContext ? 'OK' : 'NG（位置情報不可）');

    // 非表示→復帰の間に位置更新が止まっていたかを記録する（バックグラウンド制約の検証）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        this.log('画面非表示');
      } else if (this.hiddenAt) {
        this.log(`画面復帰（非表示 ${((Date.now() - this.hiddenAt) / 1000).toFixed(1)} 秒）`);
        this.hiddenAt = null;
      }
    });

    const loop = () => {
      this.frames++;
      const now = performance.now();
      if (now - this.fpsAt >= 1000) {
        this.set('fps', String(Math.round((this.frames * 1000) / (now - this.fpsAt))));
        this.frames = 0;
        this.fpsAt = now;
      }
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  toggle(force?: boolean): void {
    this.el.hidden = force === undefined ? !this.el.hidden : !force;
  }

  set(key: string, value: string): void {
    const f = this.fields.get(key);
    if (f) f.textContent = value;
  }

  recordSample(s: PositionSample): void {
    const now = Date.now();
    if (this.lastSampleAt) {
      const gap = now - this.lastSampleAt;
      this.intervals.push(gap);
      if (this.intervals.length > 60) this.intervals.shift();
      if (gap > 5000) this.log(`位置更新が ${(gap / 1000).toFixed(1)} 秒途切れた`);
    }
    this.lastSampleAt = now;
    const avg = this.intervals.reduce((a, b) => a + b, 0) / (this.intervals.length || 1);
    const max = Math.max(0, ...this.intervals);
    this.set('source', { gps: 'GPS', replay: 'GPX 再生', sim: 'テスト走行' }[s.source]);
    this.set('interval', `${(avg / 1000).toFixed(1)} / ${(max / 1000).toFixed(1)} 秒`);
    this.set('accuracy', `${s.accuracy.toFixed(0)} m`);
    this.set('speed', s.speed == null ? '—' : `${(s.speed * 3.6).toFixed(0)} km/h`);
    this.set('gpsHeading', s.heading == null || Number.isNaN(s.heading) ? '—（停車中/非対応）' : `${s.heading.toFixed(0)}°`);
  }

  log(msg: string): void {
    const t = new Date().toLocaleTimeString('ja-JP');
    this.logs.push({ t, msg });
    if (this.logs.length > 300) this.logs.shift();
    this.logEl.textContent = this.logs.slice(-12).map((l) => `${l.t} ${l.msg}`).join('\n');
  }

  private addField(key: string, label: string) {
    const dl = this.el.querySelector('dl')!;
    const dt = document.createElement('dt');
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.textContent = '—';
    dl.append(dt, dd);
    this.fields.set(key, dd);
  }

  private download() {
    const snapshot = Object.fromEntries([...this.fields].map(([k, el]) => [k, el.textContent]));
    const blob = new Blob(
      [JSON.stringify({ at: new Date().toISOString(), ua: navigator.userAgent, snapshot, logs: this.logs }, null, 2)],
      { type: 'application/json' },
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `navi-diag-${Date.now()}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
}
